import { useState, useCallback, useRef } from 'react'
import req from '@sportninja/common/api/request'
import { GameEditData, EvaluationResult } from './useGameEdit'

export interface SlotSuggestion {
  facility_uid: string
  facility_name: string
  venue_uid: string
  venue_name: string
  starts_at: string
  ends_at: string
  satisfaction_score: number
  hard_violations: number
  soft_violations: number
}

export interface MutationPreview {
  type: 'move' | 'swap' | 'edit'
  gameId: string
  secondGameId?: string
  proposedData: GameEditData
  resolvedData: GameEditData
  currentScore: number
  newScore: number
  scoreDelta: number
  hardViolations: EvaluationResult['violations']
  softViolations: EvaluationResult['violations']
  alternativeSlots: SlotSuggestion[]
}

export interface UseGameMutationResult {
  preview: MutationPreview | null
  modalVisible: boolean
  loading: boolean
  confirming: boolean
  error: string | null
  requestPreview: (
    flightId: string,
    gameId: string,
    data: GameEditData
  ) => Promise<void>
  requestSwapPreview: (
    flightId: string,
    gameAId: string,
    gameBId: string
  ) => Promise<void>
  confirmMutation: (overrideReason?: string) => Promise<boolean>
  selectAlternativeSlot: (slot: SlotSuggestion) => void
  cancelMutation: () => void
}

export const useGameMutation = (
  onSuccess?: () => void
): UseGameMutationResult => {
  const [preview, setPreview] = useState<MutationPreview | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const flightIdRef = useRef<string>('')

  const requestPreview = useCallback(
    async (flightId: string, gameId: string, data: GameEditData) => {
      setLoading(true)
      setError(null)
      flightIdRef.current = flightId

      try {
        // Run preview and suggest-slots in parallel
        const [previewRes, suggestRes] = await Promise.all([
          req(`/scheduler/flights/${flightId}/games/${gameId}/preview`, {
            method: 'POST',
            body: JSON.stringify(data),
          }),
          req(`/scheduler/flights/${flightId}/games/${gameId}/suggest-slots`, {
            method: 'POST',
            body: JSON.stringify({ max_suggestions: 5 }),
          }).catch(() => ({ suggestions: [], current_score: 0 })),
        ])

        const evaluation = previewRes?.evaluation
        const currentScore = suggestRes?.current_score ?? 0
        const newScore = evaluation?.satisfaction_score ?? currentScore

        const allViolations = evaluation?.violations ?? []
        const hardViolations = allViolations.filter(
          (v: EvaluationResult['violations'][0]) => v.severity === 'error'
        )
        const softViolations = allViolations.filter(
          (v: EvaluationResult['violations'][0]) => v.severity === 'warning'
        )

        setPreview({
          type: 'move',
          gameId,
          proposedData: data,
          resolvedData: previewRes?.resolved_data ?? data,
          currentScore,
          newScore,
          scoreDelta: newScore - currentScore,
          hardViolations,
          softViolations,
          alternativeSlots: suggestRes?.suggestions ?? [],
        })
        setModalVisible(true)
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message ||
              'Failed to preview changes'
        setError(message)
        setModalVisible(true)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const requestSwapPreview = useCallback(
    async (flightId: string, gameAId: string, gameBId: string) => {
      setLoading(true)
      setError(null)
      flightIdRef.current = flightId

      try {
        const res = await req(
          `/scheduler/flights/${flightId}/games/swap-preview`,
          {
            method: 'POST',
            body: JSON.stringify({
              game_a_uid: gameAId,
              game_b_uid: gameBId,
            }),
          }
        )

        const evaluation = res?.evaluation
        const currentScore = evaluation?.satisfaction_score ?? 0
        const newScore = evaluation?.satisfaction_score ?? currentScore

        const allViolations = evaluation?.violations ?? []
        const hardViolations = allViolations.filter(
          (v: EvaluationResult['violations'][0]) => v.severity === 'error'
        )
        const softViolations = allViolations.filter(
          (v: EvaluationResult['violations'][0]) => v.severity === 'warning'
        )

        setPreview({
          type: 'swap',
          gameId: gameAId,
          secondGameId: gameBId,
          proposedData: {},
          resolvedData: {},
          currentScore,
          newScore,
          scoreDelta: 0,
          hardViolations,
          softViolations,
          alternativeSlots: [],
        })
        setModalVisible(true)
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message || 'Failed to preview swap'
        setError(message)
        setModalVisible(true)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const confirmMutation = useCallback(
    async (overrideReason?: string): Promise<boolean> => {
      if (!preview) {
        return false
      }

      setConfirming(true)
      setError(null)

      try {
        const flightId = flightIdRef.current
        const hasHardViolations = preview.hardViolations.length > 0
        const forceData =
          hasHardViolations && overrideReason
            ? { force: true, override_reason: overrideReason }
            : {}

        if (preview.type === 'swap' && preview.secondGameId) {
          await req(`/scheduler/flights/${flightId}/games/swap`, {
            method: 'POST',
            body: JSON.stringify({
              game_a_uid: preview.gameId,
              game_b_uid: preview.secondGameId,
              ...forceData,
            }),
          })
        } else {
          await req(`/scheduler/flights/${flightId}/games/${preview.gameId}`, {
            method: 'PUT',
            body: JSON.stringify({
              ...preview.resolvedData,
              ...forceData,
            }),
          })
        }

        setModalVisible(false)
        setPreview(null)
        onSuccess?.()
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message || 'Failed to save changes'
        setError(message)
        return false
      } finally {
        setConfirming(false)
      }
    },
    [preview, onSuccess]
  )

  const selectAlternativeSlot = useCallback(
    (slot: SlotSuggestion) => {
      if (!preview) {
        return
      }

      // Re-preview with the selected alternative slot data
      const newData: GameEditData = {
        ...preview.proposedData,
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        facility_id: slot.facility_uid as unknown as number,
      }

      requestPreview(flightIdRef.current, preview.gameId, newData)
    },
    [preview, requestPreview]
  )

  const cancelMutation = useCallback(() => {
    setModalVisible(false)
    setPreview(null)
    setError(null)
  }, [])

  return {
    preview,
    modalVisible,
    loading,
    confirming,
    error,
    requestPreview,
    requestSwapPreview,
    confirmMutation,
    selectAlternativeSlot,
    cancelMutation,
  }
}

export default useGameMutation
